import { FC } from 'react';

import MDEditor from '@uiw/react-md-editor';
import { useFormik } from 'formik';
import { Bot, Gift, Save, SquareAsterisk } from 'lucide-react';
import { isShallowEqual, pick } from 'remeda';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

import css from './TelegramBotSettingsForm.module.css';

import { telegramBotService } from 'v2/api/telegramBotService';
import { Card } from 'v2/components/card/Card';
import { CardHeader } from 'v2/components/card/cardHeader/CardHeader';
import { Switch } from 'v2/components/switch/Switch';
import { ITelegramBotSettingsForm, TelegramBotSettingsModel } from 'v2/types/TelegramBotSettingsModel';
import { handleHttpError } from 'v2/utils';

interface TelegramBotSettingsFormProps {
  settings: TelegramBotSettingsModel;
  onRevalidate: VoidFunction;
}

export const TelegramBotSettingsForm: FC<TelegramBotSettingsFormProps> = ({ settings, onRevalidate }) => {
  const { isMutating, trigger } = useSWRMutation(
    'updateTelegramBotSettings',
    telegramBotService.updateTelegramBotSettings,
    {
      onSuccess: () => {
        toast.success('Settings have been successfully updated');
        onRevalidate();
      },
      onError: handleHttpError,
    }
  );

  const formik = useFormik<ITelegramBotSettingsForm>({
    initialValues: settings,
    onSubmit: (values) => {
      trigger(
        pick(values, [
          'isActiveContest',
          'isEmojiContestActive',
          'isSimpleMenu',
          'freespinsId',
          'defaultBalanceRewardAmount',
          'realslotsMirrorLink',
          'tournamentsAnnouncement',
        ])
      );
    },
  });

  const handleEditorChange = (value?: string) => {
    formik.setFieldValue('tournamentsAnnouncement', value);
  };

  return (
    <form className={css.telegramBotSettingsFormContainer} onSubmit={formik.handleSubmit}>
      <div className={css.telegramBotSettingsForm}>
        <div className={css.telegramBotSettingsContainer}>
          <Card>
            <CardHeader title="Contest Configuration" icon={Gift} />

            <Switch
              name="isActiveContest"
              checked={formik.values.isActiveContest}
              onChange={formik.handleChange}
              label="Contest Active"
            />

            <Switch
              name="isEmojiContestActive"
              checked={formik.values.isEmojiContestActive}
              onChange={formik.handleChange}
              label="Emoji Contest Active"
            />
          </Card>

          <Card>
            <CardHeader title="Bot Configuration" icon={Bot} />

            <Switch
              name="isSimpleMenu"
              checked={formik.values.isSimpleMenu}
              onChange={formik.handleChange}
              label="Simple Menu"
            />

            <div className="form">
              <TextField
                name="defaultBalanceRewardAmount"
                value={formik.values.defaultBalanceRewardAmount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label="Drop Reward (FUNS)"
                type="number"
                fullWidth
              />

              <TextField
                name="freespinsId"
                value={formik.values.freespinsId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label="Free Spins Id"
                fullWidth
              />

              <TextField
                name="realslotsMirrorLink"
                value={formik.values.realslotsMirrorLink}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label="Website Mirror"
                fullWidth
                required
              />
            </div>
          </Card>
        </div>

        <div className={css.telegramBotSettingsEditor}>
          <CardHeader title="Tournament Announcement" icon={SquareAsterisk} />

          <MDEditor
            className={css.telegramBotSettingsFormMdEditor}
            value={formik.values.tournamentsAnnouncement}
            onChange={handleEditorChange}
            height="100%"
          />
        </div>
      </div>

      <Button
        className={css.telegramBotSettingsFormSubmit}
        startIcon={<Save width={20} />}
        loading={isMutating}
        variant="contained"
        size="large"
        type="submit"
        disabled={isShallowEqual(settings, formik.values)}
      >
        Update Settings
      </Button>
    </form>
  );
};
